import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { GoogleMapsGeocodingResponse, GeocodingSuggestion } from '../models/geocoding.model';
import { COUNTRY_CODE } from '../consts/country-code.const';

const { ARUBA, CURACAO } = COUNTRY_CODE;
const COUNTRIES_WITHOUT_ZIP_CODE: string[] = [ARUBA, CURACAO];

@Injectable({
  providedIn: 'root'
})
export class GeocodingService {
  private readonly http = inject(HttpClient);

  private apiKey = environment.GOOGLE_MAPS_API_KEY;
  private baseUrl = 'https://maps.googleapis.com/maps/api/geocode/json';

  getCoordinates(address: string, country: string): Observable<GeocodingSuggestion[]> {
    const url = `${this.baseUrl}?address=${encodeURIComponent(address)}&key=${this.apiKey}&components=country:${country}`;
    return this.http.get<GoogleMapsGeocodingResponse>(url).pipe(
      map((response) => {
        return (
          response?.results?.map((result) => {
            return {
              address: result.formatted_address,
              coordinates: {
                lat: result.geometry.location.lat,
                lng: result.geometry.location.lng
              }
            };
          }) || []
        );
      })
    );
  }

  reverseGeocode(lat: number, lng: number): Observable<GeocodingSuggestion | null> {
    const url = `${this.baseUrl}?latlng=${lat},${lng}&key=${this.apiKey}`;

    return this.http.get<GoogleMapsGeocodingResponse>(url).pipe(
      map((response) => {
        const result = response?.results?.[0];
        if (!result) return null;
        const zipCode = result.address_components.find((component) => component.types.includes('postal_code'))?.long_name || '';
        const countryCode = result.address_components.find((component) => component.types.includes('country'))?.short_name || '';
        return {
          address: result.formatted_address,
          zipCode: this.usesZipCode(countryCode) ? zipCode : '',
          countryCode,
          coordinates: {
            lat: result.geometry.location.lat,
            lng: result.geometry.location.lng
          }
        };
      })
    );
  }

  usesZipCode(countryCode?: string): boolean {
    return !countryCode || !COUNTRIES_WITHOUT_ZIP_CODE.includes(countryCode);
  }
}
